import axios from "axios";
import { api, errorLogger, notify } from "assets/scripts/utilities/api-clients";

import {
  createCode,
  resendCode,
  consumeCode,
  clearLoginAttemptInfo,
  getLoginAttemptInfo,
} from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";

export const sendOTP = async ({ phone }) => {
  try {
    const response = await createCode({
      phoneNumber: `+91${phone}`,
    });

    if (response.status === "SIGN_IN_UP_NOT_ALLOWED") {
      const { message = "Oops! Something went wrong. Please try again." } =
        response;

      notify({
        message,
        type: "negative",
      });

      throw message;
    } else {
      notify({ message: "OTP sent successfully!", type: "positive" });

      if (
        !document.cookie
          .split(";")
          .some((cookie) => cookie.trim().startsWith("x-app-name="))
      ) {
        document.cookie = "x-app-name=nucleus";
      }
    }

    return response.fetchResponse;
  } catch (error) {
    if (error.isSuperTokensGeneralError === true) {
      // this may be a custom error message sent from the API by you,
      // or if the input email / phone number is not valid.
      notify({ message: error.message, type: "negative" });
    } else {
      notify({
        message: "Oops! Something went wrong. Please try again.",
        type: "negative",
      });
    }

    throw error;
  }
};

export const hasInitialOTPBeenSent = async () => {
  return (await getLoginAttemptInfo()) !== undefined;
};

export const resendOTP = async () => {
  try {
    let response = await resendCode();

    if (response.status === "RESTART_FLOW_ERROR") {
      // this can happen if the user has already successfully logged in into
      // another device whilst also trying to login to this one.

      // we clear the login attempt info that was added when the createCode function
      // was called - so that if the user does a page reload, they will now see the
      // enter email / phone UI again.
      await clearLoginAttemptInfo();

      notify({
        message: "Oops! Something went wrong. Please try again.",
        type: "negative",
      });
    } else {
      // OTP resent successfully.
      notify({ message: "OTP sent successfully!", type: "positive" });
    }
  } catch (error) {
    if (error.isSuperTokensGeneralError === true) {
      // this may be a custom error message sent from the API by you.
      notify({ message: error.message, type: "negative" });
    } else {
      notify({
        message: "Oops! Something went wrong. Please try again.",
        type: "negative",
      });
    }

    throw error;
  }
};

export const login = async ({ otp }) => {
  try {
    let response = await consumeCode({
      userInputCode: otp,
    });

    if (response.status === "OK") {
      // we clear the login attempt info that was added when the createCode function
      // was called since the login was successful.
      await clearLoginAttemptInfo();
    } else if (response.status === "INCORRECT_USER_INPUT_CODE_ERROR") {
      // the user entered an invalid OTP
      notify({
        message:
          "Wrong OTP! Please try again. Number of attempts left: " +
          (response.maximumCodeInputAttempts -
            response.failedCodeInputAttemptCount),
        type: "negative",
      });
    } else if (response.status === "EXPIRED_USER_INPUT_CODE_ERROR") {
      // it can come here if the entered OTP was correct, but has expired because
      // it was generated too long ago.
      notify({
        message: "Old OTP entered. Please regenerate a new one and try again",
        type: "negative",
      });
    } else {
      // this can happen if the user tried an incorrect OTP too many times.
      // or if it was denied due to security reasons in case of automatic account linking

      // we clear the login attempt info that was added when the createCode function
      // was called - so that if the user does a page reload, they will now see the
      // enter email / phone UI again.
      await clearLoginAttemptInfo();

      notify({
        message: "Oops! Something went wrong. Please try again.",
        type: "negative",
      });
    }

    return response.fetchResponse;
  } catch (error) {
    if (error.isSuperTokensGeneralError === true) {
      // this may be a custom error message sent from the API by you.
      notify({ message: error.message, type: "negative" });
    } else {
      notify({
        message: "Oops! Something went wrong. Please try again.",
        type: "negative",
      });
    }

    throw error;
  }
};

export const getSessionInfo = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.CLIENT_API_DOMAIN}/sessioninfo`,
    );

    return response.data;
  } catch (error) {
    errorLogger(error);

    throw error;
  }
};

export const refreshToken = async () => {
  return await Session.attemptRefreshingSession();
};

export const logout = async () => {
  try {
    await api.post("/auth/logout");

    return await Session.signOut();
  } catch (error) {
    notify({
      message: "Oops! Something went wrong. Please try again.",
      type: "negative",
    });

    throw error;
  }
};

export const getUser = async () => {
  const response = await api.get("/auth/user");

  return response.data;
};

export const getUserRoles = async () => {
  const response = await api.get("/auth/user/roles");

  return response;
};
